import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import DBModal from 'components/modal/modal';
import {FieldGroup, ModalShell} from './ui/ModalShell';
import {StatusPill} from './ui/cells';
import theme from '../ordersTheme.module.scss';
import {
  BULK_ORDER_OPERATIONS,
  CREATE_ORDER_NOTE,
  EXPIRE_ORDER,
  OPEN_ORDER,
  REJECT_ORDER,
  REVERT_OPEN_ORDER,
  REWRITE_ORDER,
  SETTLE_ORDER,
  UPDATE_ORDER_NOTE,
} from 'graphql/mutations/stocks';
import {toast} from 'react-toastify';
import styles from './viewOrders.module.scss';
import {Button} from '@dabafinance/react-components';
import {
  DatePicker,
  Form,
  Input,
  Select,
  InputNumber,
  Table,
  Typography,
  Button as AntButton,
  Spin,
  Switch,
} from 'antd';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import SubmitButton from 'components/Startup/components/SubmitButton';
import backIcon from 'images/back.png';
import {brokerOptionLabel} from '../brokerVenues';
import {BROKER_EXPORT, GET_ALL_BROKERS} from 'graphql/queries/stocks';
import {buildOrderFilterInput} from './ordersFilterInput';
import {currencySymbols, stockOrderStatus} from 'utils/mock';
import {
  extractUserId,
  formatValue,
  convertToNumber,
  isDabaEmail,
} from 'utils/helper';
import {OrderFilter} from './OrderFilter';
import {UploadVideoToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {handleOrderRewrite} from './OrderActions';
import {BackOfficeFields} from './backOfficeFields';
import FileUploadList from 'components/fileUploadList';
import {isDev} from 'utils/constants';
import ExecutionSummaryModal from './ExecutionSummaryModal';

export const BulkFillCSVOrders = ({orderModal, setOrderModal, onKeyCopied}) => {
  const [loadingCSVUpload, setLoadingCSVUpload] = useState(false);
  const [progress, setProgress] = useState(0);
  const [csvName, setCsvName] = useState('');
  const [csvUrl, setCsvUrl] = useState('');
  const [openFinishModal, setOpenFinishModal] = useState(false);

  const isFilledBulk = orderModal === 'FILL_CSV';
  const [bulkOrderOperation, {loading}] = useMutation(BULK_ORDER_OPERATIONS);

  const handleCSVUPload = async e => {
    setProgress(0);
    const selectedFile = e.target.files[0];
    setCsvName(selectedFile.name);
    setLoadingCSVUpload(true);
    const response = await UploadVideoToBucket(
      selectedFile,
      'campaign-bucket-images',
      setProgress,
    );
    setCsvUrl(response);
    setLoadingCSVUpload(false);
  };

  const handleSubmit = () => {
    bulkOrderOperation({
      variables: {
        input: {
          csv: csvUrl,
          status: isFilledBulk ? 'FILLED' : 'SETTLED',
        },
      },
    })
      .then(({data: {bulkOrderOperation}}) => {
        if (bulkOrderOperation.__typename === 'Error') {
          toast.error(
            `Something went wrong!!! - ${bulkOrderOperation.message} `,
          );
        } else {
          toast.success('CSV Uploaded Successfully');
          if (bulkOrderOperation?.data?.key) {
            const copiedKey = bulkOrderOperation?.data?.key;
            navigator.clipboard.writeText(copiedKey);
            onKeyCopied?.(copiedKey, 'Bulk CSV upload');
            toast.info('Order Book Key Copied to Clipboard Successfully');
          }
          setOrderModal(false);
          setCsvUrl('');
          setOpenFinishModal(false);
          setCsvName('');
          setProgress(0);
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };

  return (
    <DBModal
      isOpen={orderModal === 'FILL_CSV' || orderModal === 'SETTLE_CSV'}
      handleClose={() => {
        setOrderModal(false);
        setCsvUrl('');
        setCsvName('');
        setProgress(0);
        setOpenFinishModal(false);
      }}
      content={
        <ModalShell
          title={`${isFilledBulk ? 'Fill' : 'Settle'} orders from a CSV`}
          subtitle={
            openFinishModal
              ? 'The whole file is applied in one operation.'
              : 'Every row in the file is applied to the order it names. Download the sample to see the columns.'
          }
          onBack={openFinishModal ? () => setOpenFinishModal(false) : undefined}
          backLabel="Change file"
          footer={
            openFinishModal ? (
              <>
                <Button
                  onClick={() => setOpenFinishModal(false)}
                  label="Cancel"
                  type="secondary"
                />
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  label={loading ? 'Uploading…' : 'Confirm upload'}
                />
              </>
            ) : null
          }>
          {openFinishModal ? (
            <div className={styles.confirmBody}>
              <span>
                {csvName ? `"${csvName}" is ready.` : 'The file is ready.'} This
                cannot be undone once it starts.
              </span>
            </div>
          ) : (
            <Form onFinish={() => setOpenFinishModal(true)} layout="vertical">
              <div className={styles.uploadSec}>
                <label className={styles.upload} htmlFor="file-upload">
                  {loadingCSVUpload ? (
                    <div className={styles.uploading}>
                      <Spin />
                      <h3>Uploading, Please wait...</h3>
                      <span>{progress}%</span>
                    </div>
                  ) : csvName && csvUrl ? (
                    <div>{csvName}</div>
                  ) : (
                    <h2>Upload CSV File</h2>
                  )}
                  <input
                    type="file"
                    id="file-upload"
                    accept=".csv"
                    className={styles['input-file']}
                    required
                    onChange={handleCSVUPload}
                  />
                </label>
                <div>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://campaign-bucket-images.s3.amazonaws.com/1724135082567-bulk-settle-sample.csv">
                    Sample CSV
                  </a>
                </div>
              </div>
              <div className={styles.buttons}>
                <SubmitButton
                  disabled={!csvUrl || loading}
                  label="Review upload"
                />
              </div>
            </Form>
          )}
        </ModalShell>
      }
    />
  );
};

export const RejectOrder = ({
  orderModal,
  setOrderModal,
  orderId,
  refetch,
  orderIds,
  setType,
  setOrder,
  type,
  isReject,
  setSelected,
  onKeyCopied,
}) => {
  const [rejectOrder, {loading: loadingReject}] = useMutation(REJECT_ORDER);
  const [rewriteOrder, {loading: loadingRewrite}] = useMutation(REWRITE_ORDER);

  const [bulkOrderOperation, {loading: loadingBulkReject}] = useMutation(
    BULK_ORDER_OPERATIONS,
  );

  const handleUpdate = () => {
    if (!isReject)
      return handleOrderRewrite({
        rewriteOrder,
        refetch,
        setOrder,
        setOrderModal,
        orderId,
      });
    let resolver;
    if (type === 'MULTIPLE') {
      resolver = bulkOrderOperation({
        variables: {
          input: {
            orderIds,
            status: 'REJECTED',
          },
        },
      });
    } else {
      resolver = rejectOrder({
        variables: {
          orderId,
        },
      });
    }

    resolver
      .then(({data}) => {
        const resolverType =
          data?.[type === 'MULTIPLE' ? 'bulkOrderOperation' : 'rejectOrder'];
        if (resolverType.__typename === 'Error') {
          toast.error(`Something went wrong!!! - ${resolverType.message} `);
        } else {
          toast.success('Rejected Successfully');
          if (resolverType?.data?.key && type === 'MULTIPLE') {
            const copiedKey = resolverType?.data?.key;
            navigator.clipboard.writeText(copiedKey);
            onKeyCopied?.(copiedKey, 'Bulk reject');
            toast.info('Order Book Key Copied to Clipboard Successfully');
          }
          setOrderModal(false);
          refetch();
          setSelected([]);
          setOrder('');
          setType('SINGLE');
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };

  return (
    <DBModal
      isOpen={orderModal}
      handleClose={() => {
        setOrderModal(false);
        setType('SINGLE');
        setOrder();
      }}
      content={
        <div className={styles.modal}>
          <h1>{isReject ? 'Update' : 'Rewrite'} Order</h1>
          {type === 'MULTIPLE' ? (
            <span>
              Are you sure you want to{' '}
              <span className={styles.type}>REJECT {orderIds?.length}</span>{' '}
              ORDER(S)
            </span>
          ) : isReject ? (
            <span>
              Are you sure you want to{' '}
              <span className={styles.type}>REJECT</span> this Order?
            </span>
          ) : (
            <span>
              Are you sure you want to{' '}
              <span className={styles.type}>REWRITE</span> this Order? NOTE that
              this will return the order to the previous state
            </span>
          )}

          <div className={styles.buttons}>
            <Button
              type="secondary"
              label="Cancel"
              onClick={() => setOrderModal(false)}
            />
            <Button
              disabled={loadingReject || loadingBulkReject || loadingRewrite}
              label={isReject ? 'Reject order' : 'Rewrite order'}
              onClick={handleUpdate}
            />
          </div>
        </div>
      }
    />
  );
};

export const FilledOrder = ({
  orderModal,
  setOrderModal,
  orderDetails,
  refetch,
  operationType,
  orderIds,
  setType = () => {},
  setSelected = () => {},
}) => {
  const [settleOrder, {loading}] = useMutation(SETTLE_ORDER);
  const [openFailedModal, setOpenFailedModal] = useState(false);
  const [failedIds, setFailedIds] = useState([]);
  const [form] = Form.useForm();
  const {
    company,
    symbol,
    id,
    direction,
    type,
    assetCurrency,
    quantity,
    fillQuantity,
  } = orderDetails;
  const handleUpdate = values => {
    settleOrder({
      variables: {
        input: {
          status: 'SETTLED',
          orderId: id,
          direction,
          ...(values.settlementDate && {
            settlementDate: moment(values?.settlementDate).format(),
          }),
          fillQuantity: Number(quantity) - Number(fillQuantity),
          type,
          assetPrice: Number(company?.stock?.lastPrice),
          ticker: symbol,
          currency: assetCurrency,
        },
      },
    })
      .then(({data: {settleOrder}}) => {
        if (settleOrder.__typename === 'Error') {
          toast.error(`Something went wrong!!! - ${settleOrder.message} `);
        } else {
          if (settleOrder.data?.length) {
            setFailedIds(settleOrder.data);
            setOpenFailedModal(true);
          }
          toast.success('Settled Successfully');
          setOrderModal(false);
          setSelected([]);
          form.resetFields();
          setType('SINGLE');
          refetch();
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };

  return (
    <>
      <DBModal
        isOpen={orderModal}
        handleClose={() => {
          setOrderModal(false);
          setType('SINGLE');
        }}
        content={
          <div className={styles.modal}>
            <h1>Settle Order</h1>
            <span>
              Are you sure you want to{' '}
              <span className={styles.type}>
                SETTLED {operationType === 'MULTIPLE' && orderIds.length}
              </span>{' '}
              {operationType === 'MULTIPLE' ? 'Orders?' : 'this Order?'}
            </span>
            <Form form={form} onFinish={handleUpdate} layout="vertical">
              <div className={styles.settleDate}>
                <Form.Item name="settlementDate">
                  <DatePicker
                    showTime={{
                      defaultValue: moment('00:00', 'HH:mm:ss'),
                    }}
                    format={'YYYY-MM-DD HH:mm:ss'}
                  />
                </Form.Item>
              </div>
              <div className={styles.buttons}>
                <Button
                  type="secondary"
                  label="Cancel"
                  onClick={() => setOrderModal(false)}
                />
                <SubmitButton disabled={loading} label="Settle order" />
              </div>
            </Form>
          </div>
        }
      />

      {/* Failed Order Modal */}
      <ConfirmModal
        title={'Some orders failed'}
        openModal={openFailedModal}
        setOpenModal={setOpenFailedModal}
        confirmText={<span>These orders failed: {failedIds.join(', ')}</span>}
        loading={loading}
        confirmation={false}
      />
    </>
  );
};

export const OpenOrder = ({
  orderModal,
  setOrderModal,
  orderId,
  refetch,
  type,
  orderIds,
  setType = () => {},
  setSelected = () => {},
  orderDetails,
  orderType,
}) => {
  const [openOrder, {loading}] = useMutation(OPEN_ORDER);
  const [form] = Form.useForm();
  const [executionSummary, setExecutionSummary] = useState(null);
  const [showExecutionSummary, setShowExecutionSummary] = useState(false);

  const handleSummaryClose = () => {
    setShowExecutionSummary(false);
    setExecutionSummary(null);
  };
  const loggedInUserId = useSelector(state => state?.auth?.userData?.user?.id);

  const {data: getAllBrokers, loading: loadingAllBrokers} = useQuery(
    GET_ALL_BROKERS,
    {
      skip: orderType !== 'EXECUTE' || !orderModal,
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const handleUpdate = values => {
    let variables = {};
    if (orderType === 'EXECUTE') {
      const params = {
        id: orderDetails?.id,
        symbol: orderDetails?.symbol,
        quantity: orderDetails?.quantity - orderDetails?.fillQuantity,
        type: orderDetails?.type,
        direction: orderDetails?.direction,
        force: values?.force,
        price:
          Number(orderDetails?.limitPrice) +
          (orderDetails?.feeSchedule?.find(
            schedule => schedule?.feeType === 'FLOATING',
          )
            ? ((orderDetails?.feeSchedule?.find(
                schedule => schedule?.feeType === 'FLOATING',
              )?.variableFeeAmount ?? 0) *
                Number(orderDetails?.limitPrice)) /
              100
            : 0),
        limit: Number(orderDetails?.limitPrice),
        user: orderDetails?.user?.id,
        expiration: orderDetails?.expiresAt
          ? new Date(orderDetails?.expiresAt).toLocaleDateString('fr-FR')
          : null,
        ...(values?.broker && {broker: values.broker}),
        caller: loggedInUserId,
      };
      return fetch(`https://automaton.dabafinance.com/brvm/router`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        method: 'POST',
        body: JSON.stringify(params),
      })
        .then(async res => {
          const executionData = await res.json().catch(() => null);
          if (res.ok) {
            toast.success('Order Executed Successfully');
            setOrderModal(false);
            setSelected([]);
            setType('SINGLE');
            form.resetFields();
            refetch();
            setExecutionSummary(executionData);
            setShowExecutionSummary(true);
          } else {
            const errorMessage =
              executionData?.response?.data ||
              executionData?.message ||
              executionData?.data ||
              'Order Execution Failed';
            toast.error(
              typeof errorMessage === 'string'
                ? errorMessage
                : 'Order Execution Failed',
            );
          }
        })
        .catch(err => {
          const errorMessage =
            err?.response?.data ||
            err?.message ||
            (typeof err === 'string' ? err : 'Something went wrong!!');
          toast.error(
            typeof errorMessage === 'string'
              ? errorMessage
              : `Something went wrong!! - ${err}`,
          );
        });
    } else if (type === 'MULTIPLE') {
      variables = {
        orderIds,
      };
    } else {
      variables = {
        orderId,
      };
    }
    openOrder({
      variables,
    })
      .then(({data: {openOrder}}) => {
        if (openOrder.__typename === 'Error') {
          toast.error(`Something went wrong!!! - ${openOrder.message} `);
        } else {
          toast.success('Opened Successfully');
          setOrderModal(false);
          setSelected([]);
          setType('SINGLE');
          refetch();
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };

  const handleClose = () => {
    setOrderModal(false);
    setType('SINGLE');
    form.resetFields();
  };

  return (
    <>
      <DBModal
        isOpen={orderModal}
        handleClose={handleClose}
        width={orderType === 'EXECUTE' ? 600 : undefined}
        content={
          <div className={styles.modal}>
            <h1>{orderType === 'EXECUTE' ? 'Execute' : 'Open'} Order</h1>
            {orderType === 'EXECUTE' ? (
              <Form
                className={styles.executeForm}
                form={form}
                layout="vertical"
                onFinish={handleUpdate}>
                <Form.Item label="Select Broker (Optional)" name="broker">
                  <Select
                    allowClear
                    showSearch
                    loading={loadingAllBrokers}
                    placeholder="Select a broker">
                    {getAllBrokers?.getBrokers?.data?.map(broker => (
                      <Select.Option key={broker?.id} value={broker?.id}>
                        {brokerOptionLabel(broker)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  rules={[{required: true, message: 'Force is required'}]}
                  initialValue={false}
                  tooltip="ensures that an order is executed irrespective of the router's decision e.g force a buy order placement even when a sell order for the same stock already exists on the same broker"
                  label="Force"
                  name="force">
                  <Switch />
                </Form.Item>
                <div className={styles.buttons}>
                  <Button
                    type="secondary"
                    label="Cancel"
                    onClick={handleClose}
                  />
                  <SubmitButton disabled={loading} label="Execute order" />
                </div>
              </Form>
            ) : (
              <>
                <span>
                  Are you sure you want to{' '}
                  <span className={styles.type}>
                    {orderType === 'EXECUTE' ? 'EXECUTE' : 'OPEN'}{' '}
                    {type === 'MULTIPLE' && orderIds.length}
                  </span>{' '}
                  {type === 'MULTIPLE' ? 'Orders?' : 'this Order?'}
                </span>
                <div className={styles.buttons}>
                  <Button
                    type="secondary"
                    label="Cancel"
                    onClick={handleClose}
                  />
                  <Button
                    disabled={loading}
                    label={
                      orderType === 'EXECUTE' ? 'Execute order' : 'Open order'
                    }
                    onClick={handleUpdate}
                  />
                </div>
              </>
            )}
          </div>
        }
      />
      <ExecutionSummaryModal
        open={showExecutionSummary}
        data={executionSummary}
        onClose={handleSummaryClose}
      />
    </>
  );
};

export const RevertOpenOrder = ({
  orderModal,
  setOrderModal,
  order,
  refetch,
  type,
  orderIds,
  setType,
  setOrder,
  setSelected,
}) => {
  const isExpire = order?.type === 'EXPIRE';
  const [pendOrder, {loading}] = useMutation(REVERT_OPEN_ORDER);
  const [expireOrder, {loading: loadingExpire}] = useMutation(EXPIRE_ORDER);
  const resolver = isExpire ? expireOrder : pendOrder;

  const handleUpdate = () => {
    let variables = {};
    if (type === 'MULTIPLE') {
      variables = {
        orderIds,
      };
    } else {
      variables = {
        orderId: isExpire ? order?.id : order,
      };
    }

    resolver({
      variables,
    })
      .then(({data}) => {
        const type = data?.[isExpire ? 'expireOrder' : 'pendOrder'];
        if (type.__typename === 'Error') {
          toast.error(`Something went wrong!!! - ${type.message} `);
        } else {
          toast.success(
            isExpire
              ? 'Order(s) Expired Successfully'
              : 'Reverted to Pending Successfully',
          );
          setOrderModal(false);
          setSelected([]);
          setOrder('');
          setType('SINGLE');
          refetch();
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };
  return (
    <DBModal
      isOpen={orderModal}
      handleClose={() => {
        setOrderModal(false);
        setType('SINGLE');
        setOrder('');
      }}
      content={
        <div className={styles.modal}>
          {isExpire ? (
            <>
              <h1>Expire Order(s)</h1>
              <span>
                Are you sure you want to Expire{' '}
                {type === 'MULTIPLE' ? 'these' : 'this'}{' '}
                <span className={styles.type}>
                  {type === 'MULTIPLE' && orderIds.length}
                </span>{' '}
                Order(s)?
              </span>
            </>
          ) : (
            <>
              <h1>Revert to pending</h1>
              <span>
                Are you sure you want to revert{' '}
                {type === 'MULTIPLE' ? 'these' : 'this'}{' '}
                <span className={styles.type}>
                  {type === 'MULTIPLE' && orderIds.length} OPEN
                </span>{' '}
                Order to Pending?
              </span>
            </>
          )}

          <div className={styles.buttons}>
            <Button
              type="secondary"
              label="Cancel"
              onClick={() => setOrderModal(false)}
            />
            <Button
              disabled={loading || loadingExpire}
              label={isExpire ? 'Expire order(s)' : 'Revert to pending'}
              onClick={handleUpdate}
            />
          </div>
        </div>
      }
    />
  );
};

export const OrderHistory = ({orderModal, setOrderModal, history}) => {
  const navigate = useNavigate();

  const columns = [
    {
      dataIndex: 'status',
      key: 'status',
      title: 'Status',
      // Same pill vocabulary as the queue behind it, rather than antd's
      // default tag palette.
      render: (text, render) => <StatusPill status={render?.status} />,
    },
    {dataIndex: 'description', key: 'description', title: 'Description'},
    {
      dataIndex: 'matchOrder',
      key: 'matchOrder',
      title: 'Match Order',
      render: (text, record) =>
        record?.matchOrder && (
          <span
            className={styles.matchOrder}
            onClick={() =>
              navigate(`/dashboard/stocks/view-order/${record.matchOrder}`)
            }>
            View Order
          </span>
        ),
    },

    {
      dataIndex: 'Price Filled',
      key: 'Price Filled',
      title: 'Price Filled',
      render: (text, record) => (
        <span>
          {currencySymbols[record?.input?.currency]?.symbol}
          {record?.input?.assetPrice}
        </span>
      ),
    },
    {
      dataIndex: 'Quantity Filled',
      key: 'Quantity Filled',
      title: 'Quantity Filled',
      render: (text, record) => <span>{record?.input?.fillQuantity}</span>,
    },
    {
      dataIndex: 'Fee',
      key: 'Fee',
      title: 'Fees',
      render: (text, record) => <span>{record?.input?.orderFees}</span>,
    },
    {
      dataIndex: 'Date',
      key: 'Date',
      title: 'Date',
      render: (text, record) => (
        <span>{moment(record?.createdAt).format('DD MMM, YYYY h:mmA')}</span>
      ),
    },
  ];
  return (
    <DBModal
      isOpen={orderModal}
      handleClose={() => setOrderModal(false)}
      width={1000}
      content={
        <ModalShell
          title="Order history"
          subtitle="Every state this order has passed through, newest last.">
          <Table
            className="antd-table"
            columns={columns}
            dataSource={history}
          />
        </ModalShell>
      }
    />
  );
};

export const OrderNotes = ({orderModal, setOrderModal, notes, refetch}) => {
  const [createNoteModal, setCreateNoteModal] = useState(false);
  const [finalizeState, setFinalizeState] = useState({});
  const [form] = Form.useForm();

  const [addOrderNote, {loading}] = useMutation(CREATE_ORDER_NOTE);
  const [updateOrderNote, {loading: loadingUpdate}] =
    useMutation(UPDATE_ORDER_NOTE);

  useEffect(() => {
    form.setFieldsValue({
      note: finalizeState?.note,
      files: finalizeState?.files
        ? finalizeState?.files?.map(file => ({
            url: file?.url,
            type: file?.type,
            name: file?.name,
          }))
        : null,
    });
  }, [finalizeState]);

  const handleCreateNote = () => {
    if (finalizeState?.noteId) {
      updateOrderNote({
        variables: {
          input: {
            orderId: notes?.id,
            note: finalizeState.note,
            noteId: finalizeState?.noteId,
            files: finalizeState?.files,
          },
        },
      })
        .then(({data: updateOrderNote}) => {
          if (updateOrderNote.__typename === 'Error') {
            toast.error(
              `Something went wrong!!! - ${updateOrderNote.message} `,
            );
          } else {
            toast.success('Order Note Updated Successfully');
            setFinalizeState({open: false});
            setCreateNoteModal(false);
            setOrderModal(false);
            form.resetFields();
            refetch();
          }
        })
        .catch(err => toast.error(`Something went wrong!! - ${err}`));
    } else {
      addOrderNote({
        variables: {
          orderId: notes?.id,
          note: finalizeState.note,
          files: finalizeState?.files,
        },
      })
        .then(({data: addOrderNote}) => {
          if (addOrderNote.__typename === 'Error') {
            toast.error(`Something went wrong!!! - ${addOrderNote.message} `);
          } else {
            toast.success('Order Note Added Successfully');
            setFinalizeState({open: false});
            setCreateNoteModal(false);
            setOrderModal(false);
            refetch();
          }
        })
        .catch(err => toast.error(`Something went wrong!! - ${err}`));
    }
  };

  const columns = [
    {
      dataIndex: 'createdBy',
      key: 'createdBy',
      title: 'Created By',
      render: (text, render) => <div>{render?.createdBy?.email}</div>,
    },
    {
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      title: 'Updated By',
      render: (text, render) => <div>{render?.updatedBy?.email}</div>,
    },
    {dataIndex: 'note', key: 'note', title: 'Note'},
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      title: 'Created At',
      render: (text, record) => (
        <span>
          {record?.createdAt &&
            moment(record?.createdAt).format('DD MMM, YYYY h:mmA')}
        </span>
      ),
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      title: 'Updated At',
      render: (text, record) => (
        <span>
          {record?.updatedAt &&
            moment(record?.updatedAt).format('DD MMM, YYYY h:mmA')}
        </span>
      ),
    },
    {
      dataIndex: 'action',
      key: 'action',
      title: 'Action',
      render: (text, render) => (
        <Button
          label="Update"
          type="secondary"
          onClick={() => {
            setFinalizeState({
              note: render?.note,
              noteId: render?.id,
              files: render?.files,
            });
            setCreateNoteModal(true);
          }}
        />
      ),
    },
  ];
  return (
    <DBModal
      isOpen={orderModal}
      handleClose={() => {
        setCreateNoteModal(false);
        setOrderModal(false);
        setFinalizeState({open: false});
      }}
      width={960}
      content={
        createNoteModal ? (
          <div className={styles.createNote}>
            <div className={styles.createTop}>
              {!finalizeState.open && (
                <img
                  onClick={() => setCreateNoteModal(false)}
                  src={backIcon}
                  alt=""
                />
              )}
              <h1>
                {' '}
                {finalizeState?.noteId ? 'Update' : 'Create'} Order Note{' '}
              </h1>
            </div>
            <Form
              onFinish={values =>
                setFinalizeState(prev => ({
                  ...prev,
                  note: values.note,
                  files: values.files,
                  open: true,
                }))
              }
              form={form}
              layout="vertical">
              {finalizeState?.open ? (
                <div className={styles.buttons}>
                  <Button
                    disabled={loading || loadingUpdate}
                    label="Yes"
                    onClick={handleCreateNote}
                  />
                  <Button
                    type="secondary"
                    label="No"
                    onClick={() =>
                      setFinalizeState(prev => ({...prev, open: false}))
                    }
                  />
                </div>
              ) : (
                <>
                  <Form.Item
                    className={styles.width}
                    rules={[
                      {required: true, message: 'This is a required field'},
                    ]}
                    name="note"
                    initialValue={finalizeState?.note}
                    label="Note">
                    <Input.TextArea rows={6} />
                  </Form.Item>
                  <FileUploadList
                    form={form}
                    name="files"
                    label="Files"
                    bucket={
                      isDev
                        ? 'transaction-receipts-bucket-nonprod-02'
                        : 'transaction-receipts-bucket-01'
                    }
                    required={false}
                    initialFiles={finalizeState?.files}
                  />
                  <div className={styles.buttons}>
                    <SubmitButton
                      label={finalizeState?.noteId ? 'Update note' : 'Add note'}
                    />
                  </div>
                </>
              )}
            </Form>
          </div>
        ) : (
          <ModalShell
            title="Order notes"
            subtitle="Notes are visible to anyone working this order."
            footer={
              notes?.notes ? (
                <Button
                  label="Add note"
                  onClick={() => {
                    setFinalizeState({
                      noteId: null,
                      note: '',
                      files: null,
                    });
                    form.resetFields();
                    setCreateNoteModal(true);
                  }}
                  type="secondary"
                />
              ) : null
            }>
            {notes.notes ? (
              <Table columns={columns} dataSource={notes?.notes} />
            ) : (
              <div className={theme.empty}>
                <h3>No notes yet</h3>
                <p>
                  Nothing has been recorded against this order. Add one to leave
                  context for whoever picks it up next.
                </p>
                <div className={styles.buttons}>
                  <Button
                    label="Add note"
                    onClick={() => {
                      setFinalizeState({
                        noteId: null,
                        note: '',
                        files: null,
                      });
                      form.resetFields();
                      setCreateNoteModal(true);
                    }}
                    type="secondary"
                  />
                </div>
              </div>
            )}
          </ModalShell>
        )
      }
    />
  );
};

const EditableCell = ({
  editing,
  dataIndex,
  inputType,
  select,
  date,
  options,
  children,
  ...restProps
}) => {
  const inputNode = select ? (
    <Select>
      {options?.map(data => (
        <Select.Option key={data.key}>{data.name}</Select.Option>
      ))}
    </Select>
  ) : inputType === 'number' ? (
    <InputNumber />
  ) : inputType === 'text' ? (
    <Input />
  ) : (
    <DatePicker
      defaultValue={
        date ? moment(date || '').format('YYYY-MM-DD HH:mm:ss') : ''
      }
      showTime={{
        defaultValue: moment('00:00', 'HH:mm:ss'),
      }}
      format={'YYYY-MM-DD HH:mm:ss'}
    />
  );
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: false,
            },
          ]}>
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export const BulkFillOrder = ({
  orderModal,
  setOrderModal,
  refetch,
  orders,
  setOrders,
  setSelected,
  onKeyCopied,
}) => {
  const [bulkOrderOperation, {loading}] = useMutation(BULK_ORDER_OPERATIONS, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const [editingKey, setEditingKey] = useState('');
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openFailedModal, setOpenFailedModal] = useState(false);
  const [failedIds, setFailedIds] = useState([]);
  const [form] = Form.useForm();
  const [updateAllForm] = Form.useForm();
  const isEditing = record => record.key === editingKey;
  const isOrderOpen = orders?.some(data => data?.status === 'OPEN');
  const isFilledModal = orderModal?.type === 'FILLED';
  const [selectedRows, setSelectedRows] = useState([]);
  const quantityColumnValue = Form.useWatch('fillQuantityLeft', updateAllForm);
  const assetPriceColumnValue = Form.useWatch('limitPrice', updateAllForm);
  const statusColumnValue = Form.useWatch('status', updateAllForm);
  const lastFillDateColumn = Form.useWatch('lastFillDate', updateAllForm);
  const settlementDateColumn = Form.useWatch('settlementDate', updateAllForm);

  const handleUpdate = () => {
    let variables = {};
    if (isFilledModal) {
      variables = {
        input: {
          settlements: orders.map(data => ({
            orderId: data?.id,
            direction: data?.direction,
            fillQuantity: Number(data?.fillQuantity),
            type: data?.type,
            assetPrice: Number(data?.limitPrice),
            ticker: data?.symbol,
            currency: data?.assetCurrency,
            ...(data?.settlementDate && {
              settlementDate: moment(data?.settlementDate).format(),
            }),
            status: 'SETTLED',
          })),
        },
      };
    } else {
      variables = {
        input: {
          status: 'FILLED',
          settlements: orders.map(data => ({
            orderId: data?.id,
            direction: data?.direction,
            fillQuantity: data?.fillQuantityLeft
              ? Number(data?.fillQuantityLeft)
              : data?.quantity - data?.fillQuantity,
            type: data?.type,
            assetPrice: Number(data?.limitPrice),
            ticker: data?.symbol,
            currency: data?.assetCurrency,
            lastFillDate: data?.lastFillDate === '' ? null : data?.lastFillDate,
            status: data?.status,
          })),
        },
      };
    }
    bulkOrderOperation({
      variables,
    })
      .then(({data: {bulkOrderOperation}}) => {
        if (bulkOrderOperation.__typename === 'Error') {
          toast.error(
            `Something went wrong!!! - ${bulkOrderOperation.message} `,
          );
        } else {
          toast.success(bulkOrderOperation?.message);
          if (Array.isArray(bulkOrderOperation?.data)) {
            setFailedIds(bulkOrderOperation.data);
            setOpenFailedModal(true);
          }
          if (bulkOrderOperation?.data?.key) {
            const copiedKey = bulkOrderOperation?.data?.key;
            navigator.clipboard.writeText(copiedKey);
            onKeyCopied?.(copiedKey, 'Bulk fill/update');
            toast.info('Order Book Key Copied to Clipboard Successfully');
          }
          setOrderModal(false);
          setOrders([]);
          updateAllForm.resetFields();
          setOpenConfirmation(false);
          setSelected([]);
          refetch();
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };

  const edit = record => {
    const fillQuantity = record?.quantity - record?.fillQuantity;
    form.setFieldsValue({
      limitPrice: record?.limitPrice,
      fillQuantityLeft: record?.fillQuantityLeft
        ? record?.fillQuantityLeft
        : fillQuantity,
      status: record?.status,
      lastFillDate: record?.lastFillDate ? moment(record?.lastFillDate) : '',
      settlementDate: record?.settlementDate
        ? moment(record?.settlementDate)
        : '',
      ticker: record?.symbol,
      direction: record?.direction,
      type: record?.type,
      assetCurrency: record?.assetCurrency,
    });
    setEditingKey(record.key);
  };

  const save = async key => {
    try {
      const row = await form.validateFields();
      const newData = [...orders];
      const index = newData.findIndex(item => key === item.key);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setOrders(newData);
        setEditingKey('');
      } else {
        newData.push(row);
        setOrders(newData);
        setEditingKey('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const cancel = () => {
    setEditingKey('');
  };

  const columns = [
    {
      title: 'Ticker',
      dataIndex: 'ticker',
      width: '10%',
      editable: false,
      render: (text, record) => <span>{record?.symbol}</span>,
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      width: '10%',
      editable: false,
    },
    {
      title: 'Order Type',
      dataIndex: 'type',
      width: '10%',
      editable: false,
    },
    {
      title: 'Currency',
      dataIndex: 'assetCurrency',
      width: '10%',
      editable: false,
    },
    {
      title: isFilledModal ? 'Filled Price' : `Asset Price`,
      dataIndex: 'limitPrice',
      width: '15%',
      editable: orderModal?.type !== 'FILLED',
      render: (text, record) => (
        <span>
          {isFilledModal
            ? record?.avgFillPrice || record?.limitPrice
            : record?.limitPrice}
        </span>
      ),
    },
    {
      title: isFilledModal ? 'Fill Quantity' : 'Fill Quantity Left',
      dataIndex: 'fillQuantityLeft',
      width: '10%',
      editable: !isFilledModal,
      render: (text, record) =>
        isFilledModal
          ? record?.fillQuantity
          : record?.fillQuantityLeft
          ? record?.fillQuantityLeft
          : Number(record?.quantity) - Number(record?.fillQuantity),
    },
    {
      title: `Order Status`,
      dataIndex: 'status',
      width: '15%',
      editable: orderModal?.type !== 'FILLED',
      render: (text, record) => record?.status?.replace('_', ' '),
    },
    {
      title: isFilledModal ? `Settlement Date` : `Filled Date`,
      dataIndex: isFilledModal ? `settlementDate` : `lastFillDate`,
      width: '20%',
      editable: true,
      render: (text, record) =>
        isFilledModal ? (
          <span>
            {record?.settlementDate
              ? moment(record?.settlementDate).format('YYYY-MM-DD HH:mm:ss')
              : ''}
          </span>
        ) : (
          <span>
            {record?.lastFillDate
              ? moment(record?.lastFillDate).format('YYYY-MM-DD HH:mm:ss')
              : ''}
          </span>
        ),
    },
    {
      title: 'operation',
      dataIndex: 'operation',
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link
              onClick={() => save(record.key)}
              style={{
                marginRight: 8,
              }}>
              Save
            </Typography.Link>
            <Typography.Link onClick={cancel}>Cancel</Typography.Link>
          </span>
        ) : (
          <Typography.Link
            disabled={editingKey !== ''}
            onClick={() => edit(record)}>
            Edit
          </Typography.Link>
        );
      },
    },
  ];

  const disableBulkButton = editingKey.length || isOrderOpen || !orders?.length;

  const mergedColumns = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: record => ({
        record,
        inputType:
          col.dataIndex === 'limitPrice' || col.dataIndex === 'fillQuantity'
            ? 'number'
            : ['lastFillDate', 'settlementDate'].includes(col.dataIndex)
            ? 'date'
            : 'text',
        select: col.dataIndex === 'status',
        options:
          col.dataIndex === 'status' &&
          stockOrderStatus.filter(data =>
            ['PARTIALLY_FILLED', 'FILLED'].includes(data.key),
          ),
        dataIndex: col.dataIndex,
        title: col.title,
        date: moment(record?.settlementDate || record?.lastFillDate || ''),
        editing: isEditing(record),
      }),
    };
  });

  const handleRemoveSelectedOrders = () => {
    const filteredOrders = orders.filter(data =>
      selectedRows.every(row => row.id !== data.id),
    );
    setSelected(filteredOrders?.map(data => data?.id));
    setOrders(filteredOrders);
  };

  return (
    <>
      <DBModal
        isOpen={orderModal?.isOpen}
        handleClose={() => {
          setOrderModal(false);
          setSelectedRows([]);
          updateAllForm.resetFields();
        }}
        width={1200}
        content={
          <ModalShell
            title={isFilledModal ? 'Settle orders' : 'Fill orders'}
            subtitle={`${orders?.length ?? 0} order${
              orders?.length === 1 ? '' : 's'
            } selected. Edit a row inline, or set one value across every row above.`}>
            <div className={styles.bulkTop}>
              <div className={styles.selectedRow}>
                <Button
                  label="Remove Selected Row(s)"
                  disabled={!selectedRows?.length}
                  onClick={handleRemoveSelectedOrders}
                />
              </div>
              <Form
                form={updateAllForm}
                className={styles.allUpdate}
                layout="vertical">
                {!isFilledModal ? (
                  <>
                    <div className={styles.allField}>
                      <Form.Item
                        className={styles.updateAllFields}
                        name="limitPrice"
                        label="Asset Price">
                        <Input type="number" />
                      </Form.Item>
                      <AntButton
                        onClick={() => {
                          const updatedOrders = orders.map(order => {
                            return {
                              ...order,
                              limitPrice: assetPriceColumnValue
                                ? Number(assetPriceColumnValue)
                                : order?.limitPrice,
                            };
                          });
                          setOrders(updatedOrders);
                        }}
                        className={styles.allButton}>
                        Update
                      </AntButton>
                    </div>
                    <div className={styles.allField}>
                      <Form.Item
                        className={styles.updateAllFields}
                        name="fillQuantityLeft"
                        label="Fill Quantity">
                        <Input type="number" />
                      </Form.Item>
                      <AntButton
                        onClick={() => {
                          const updatedOrders = orders.map(order => {
                            const fillQuantity =
                              order?.quantity - order?.fillQuantity;
                            return {
                              ...order,
                              fillQuantityLeft: quantityColumnValue
                                ? Number(quantityColumnValue)
                                : fillQuantity,
                            };
                          });
                          setOrders(updatedOrders);
                        }}
                        className={styles.allButton}>
                        Update
                      </AntButton>
                    </div>
                    <div className={styles.allField}>
                      <Form.Item
                        className={styles.updateAllFields}
                        name="status"
                        label="Status">
                        <Select allowClear>
                          {stockOrderStatus
                            .filter(status =>
                              ['PARTIALLY_FILLED', 'FILLED'].includes(
                                status.key,
                              ),
                            )
                            .map(data => (
                              <Select.Option key={data.key}>
                                {data.name}
                              </Select.Option>
                            ))}
                        </Select>
                      </Form.Item>
                      <AntButton
                        onClick={() => {
                          const updatedOrders = orders.map(order => ({
                            ...order,
                            status: statusColumnValue
                              ? statusColumnValue
                              : order?.status,
                          }));
                          setOrders(updatedOrders);
                        }}
                        className={styles.allButton}>
                        Update
                      </AntButton>
                    </div>
                    <div className={styles.allField}>
                      <Form.Item
                        className={styles.updateAllFields}
                        name="lastFillDate"
                        label="Fill Date">
                        <DatePicker
                          showTime={{
                            defaultValue: moment('00:00', 'HH:mm:ss'),
                          }}
                          format={'YYYY-MM-DD HH:mm:ss'}
                        />
                      </Form.Item>
                      <AntButton
                        onClick={() => {
                          const updatedOrders = orders.map(order => ({
                            ...order,
                            ...(lastFillDateColumn && {
                              lastFillDate: moment(lastFillDateColumn),
                            }),
                          }));
                          setOrders(updatedOrders);
                        }}
                        className={styles.allButton}>
                        Update
                      </AntButton>
                    </div>
                  </>
                ) : (
                  <div className={styles.allField}>
                    <Form.Item
                      className={styles.updateAllFields}
                      name="settlementDate"
                      label="Settlement Date">
                      <DatePicker
                        showTime={{
                          defaultValue: moment('00:00', 'HH:mm:ss'),
                        }}
                        format={'YYYY-MM-DD HH:mm:ss'}
                      />
                    </Form.Item>
                    <AntButton
                      onClick={() => {
                        const updatedOrders = orders.map(order => ({
                          ...order,
                          ...(settlementDateColumn && {
                            settlementDate: moment(settlementDateColumn),
                          }),
                        }));
                        setOrders(updatedOrders);
                      }}
                      className={styles.allButton}>
                      Update
                    </AntButton>
                  </div>
                )}
              </Form>
            </div>

            <Form
              form={form}
              component={false}
              className={styles.modalForm}
              onFinish={() => setOpenConfirmation(true)}>
              <Table
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                bordered
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedRows?.map(data => data?.id),
                  onChange: (selectedRowKeys, selectedRows) => {
                    setSelectedRows(selectedRows);
                  },
                }}
                dataSource={orders}
                columns={mergedColumns}
                rowClassName="editable-row"
                pagination={{
                  onChange: cancel,
                }}
              />
              <div className={styles.buttons}>
                {isOrderOpen && (
                  <p className={styles.bulkWarning}>
                    An order cannot be open — open orders must be filled before
                    they can be settled.
                  </p>
                )}
                <Button
                  type="primary"
                  onClick={() => setOpenConfirmation(true)}
                  label={isFilledModal ? 'Settle orders' : 'Fill orders'}
                  loading={loading}
                  disabled={disableBulkButton}
                />
              </div>
            </Form>
          </ModalShell>
        }
      />

      {/* Confirmation modal */}
      <ConfirmModal
        title={isFilledModal ? 'Confirm bulk settle' : 'Confirm bulk fill'}
        openModal={openConfirmation}
        setOpenModal={() => {
          setOpenConfirmation(false);
          updateAllForm.resetFields();
        }}
        confirmText={
          <span>
            Are you sure you want to{' '}
            <span className={styles.type}>
              BULK {isFilledModal ? 'SETTLED' : 'FILL'}
            </span>{' '}
            these <span className={styles.type}>{orders.length}</span> orders?
          </span>
        }
        loading={loading}
        handleOperation={handleUpdate}
      />

      {/* Failed Order Modal */}
      <ConfirmModal
        title={'Some orders failed'}
        openModal={openFailedModal}
        setOpenModal={() => {
          setOpenConfirmation(false);
          updateAllForm.resetFields();
        }}
        confirmText={<span>These orders failed: {failedIds.join(', ')}</span>}
        loading={loading}
        confirmation={false}
      />
    </>
  );
};

export const ExportBroker = ({
  orderModal,
  activeTab,
  setOrderModal,
  modalType,
}) => {
  const {data, loading: brokerLoading} = useQuery(GET_ALL_BROKERS, {
    skip: !orderModal,
  });
  const [exportOrdersForBroker, {loading}] = useLazyQuery(BROKER_EXPORT);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [user, setUser] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [form] = Form.useForm();

  const {Option} = Select;

  useEffect(() => {
    form.resetFields();
    setUser(null);
  }, [activeTab]);

  const handleUpdate = () => {
    const {
      startDate,
      endDate,
      collection,
      collectionId,
      startExpiryDate,
      endExpiryDate,
      brokerFormat,
      broker,
      recipient,
      type,
      direction,
      bundle,
      placed,
      status,
      startSettlementDate,
      endSettlementDate,
      startFillDate,
      hold,
      endFillDate,
      affiliateOrder,
      stockExchange,
      historyUrl,
      statementUrl,
      instrumentKey,
      instrumentId,
      quantityGreaterThan,
      quantityLessThan,
      limitPriceLessThan,
      limitPriceGreaterThan,
      assetPriceOverFloated,
    } = filterValues;

    const formattedValues = {
      ...(user && {userId: extractUserId(user)}),
      startDate: formatValue(startDate, d => moment(d).format()),
      endDate: formatValue(endDate, d => moment(d).format()),
      startExpiryDate: formatValue(startExpiryDate, d => moment(d).format()),
      endExpiryDate: formatValue(endExpiryDate, d => moment(d).format()),
      startSettlementDate: formatValue(startSettlementDate, d =>
        moment(d).format(),
      ),
      endSettlementDate: formatValue(endSettlementDate, d =>
        moment(d).format(),
      ),
      startFillDate: formatValue(startFillDate, d => moment(d).format()),
      endFillDate: formatValue(endFillDate, d => moment(d).format()),
      quantityGreaterThan: convertToNumber(quantityGreaterThan),
      quantityLessThan: convertToNumber(quantityLessThan),
      limitPriceLessThan: convertToNumber(limitPriceLessThan),
      limitPriceGreaterThan: convertToNumber(limitPriceGreaterThan),
      assetPriceOverFloated,
    };

    exportOrdersForBroker({
      variables: {
        input: {
          // Everything the shared filter panel collects, by declared type, so
          // a field added to the panel reaches the export too. The export's
          // own controls follow and win: `broker` and the typed `recipient`
          // emails are this modal's, not the panel's.
          ...buildOrderFilterInput(filterValues, {isExport: true}),
          brokerId: broker,
          recipients: recipient,
          brokerFormat,
          status: status?.length >= 1 ? status : null,
          ...formattedValues,
          collection,
          affiliateOrder,
          historyUrl,
          statementUrl,
          stockExchange,
          ...(modalType === 'BACK_OFFICE'
            ? {
                backoffice: true,
              }
            : {
                ...(bundle !== undefined && {bundle}),
                ...(placed !== undefined && {placed}),
              }),
          instrumentKey,
          instrumentId,
          collectionId,
          type,
          direction,
          hold,
        },
      },
    })
      .then(({data: {exportOrdersForBroker}}) => {
        if (exportOrdersForBroker.__typename === 'Error') {
          toast.error(
            `Something went wrong!!! - ${exportOrdersForBroker.message} `,
          );
        } else {
          toast.success('Export has been processed');
          setOrderModal({open: false, type: null});
          form.resetFields();
          setUser('');
          setFilterValues({});
          setOpenConfirmModal(false);
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };

  const validateEmail = (rule, value, callback) => {
    if (!value) {
      callback();
    } else {
      if (!isDabaEmail(value)) {
        callback('Please use an official email');
      } else {
        callback();
      }
    }
  };

  return (
    <>
      <DBModal
        isOpen={orderModal}
        handleClose={() => {
          setOrderModal({open: false, type: null});
        }}
        width={720}
        content={
          <Form
            form={form}
            onFinish={values => {
              setOpenConfirmModal(true);
              setFilterValues(values);
            }}
            layout="vertical">
            <ModalShell
              title={`Broker ${
                modalType === 'ORDER' ? 'order' : 'back office'
              } export`}
              subtitle={
                modalType === 'BACK_OFFICE'
                  ? 'Upload the broker history and statement, then set the window the export covers.'
                  : 'The export runs in the background and is sent to the address you give here.'
              }
              footer={
                <SubmitButton loading={loading} label={'Proceed to export'} />
              }>
              <FieldGroup title="Broker">
                <Form.Item
                  name="broker"
                  label="Broker"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a broker',
                    },
                  ]}>
                  <Select loading={brokerLoading}>
                    {data?.getBrokers?.data.map(item => (
                      <Option key={item.id} value={item.id}>
                        {brokerOptionLabel(item)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="brokerFormat"
                  label="Broker Format"
                  rules={[
                    {
                      required: modalType === 'BACK_OFFICE',
                      message: 'Please select a broker',
                    },
                  ]}>
                  <Select loading={brokerLoading}>
                    {data?.getBrokers?.data.map(item => (
                      <Option key={item.id} value={item.id}>
                        {brokerOptionLabel(item)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="status"
                  rules={[
                    {
                      required: modalType === 'BACK_OFFICE',
                      message: 'Please select a status',
                    },
                  ]}
                  label="Status(es)">
                  <Select mode="tags">
                    {modalType === 'BACK_OFFICE'
                      ? stockOrderStatus
                          .filter(data =>
                            ['PARTIALLY_FILLED', 'OPEN'].includes(data.key),
                          )
                          .map(data => (
                            <Select.Option key={data.key}>
                              {data.name}
                            </Select.Option>
                          ))
                      : stockOrderStatus.map(data => (
                          <Select.Option key={data.key}>
                            {data.name}
                          </Select.Option>
                        ))}
                  </Select>
                </Form.Item>
                {modalType !== 'BACK_OFFICE' && (
                  <Form.Item
                    name="recipient"
                    label="Official Email Address"
                    rules={[
                      {
                        required: true,
                        message: 'Please input a valid daba email',
                      },
                      {validator: validateEmail},
                    ]}>
                    <Input placeholder="name@dabafinance.com" />
                  </Form.Item>
                )}
              </FieldGroup>

              {modalType === 'BACK_OFFICE' ? (
                <FieldGroup title="Back office files" columns={1}>
                  <BackOfficeFields form={form} />
                </FieldGroup>
              ) : (
                <FieldGroup
                  title="Which orders"
                  columns={1}
                  description="Leave a field empty to place no restriction on it.">
                  <OrderFilter
                    isFilterModalOpen={orderModal}
                    isExport={true}
                    user={user}
                    exportForm={form}
                    setUser={setUser}
                  />
                </FieldGroup>
              )}
            </ModalShell>
          </Form>
        }
      />
      <ConfirmModal
        title={'Confirm export'}
        openModal={openConfirmModal}
        setOpenModal={setOpenConfirmModal}
        confirmText={<span>Are you sure you want to EXPORT these orders?</span>}
        loading={loading}
        handleOperation={handleUpdate}
      />
    </>
  );
};

/**
 * The confirmation every destructive action on this screen routes through.
 *
 * `confirmation={false}` turns it into an acknowledgement — one Okay button,
 * no operation to run — which is how failed-order lists are shown.
 */
export const ConfirmModal = ({
  title,
  openModal,
  setOpenModal,
  confirmText,
  loading,
  handleOperation,
  confirmation = true,
}) => {
  return (
    <DBModal
      isOpen={openModal}
      handleClose={() => setOpenModal(false)}
      content={
        <ModalShell
          title={title}
          footer={
            confirmation ? (
              <>
                <Button
                  onClick={() => setOpenModal(false)}
                  type="secondary"
                  label="Cancel"
                />
                <Button
                  disabled={loading}
                  onClick={handleOperation}
                  label={loading ? 'Working…' : 'Confirm'}
                />
              </>
            ) : (
              <Button
                onClick={() => setOpenModal(false)}
                type="primary"
                label="Okay"
              />
            )
          }>
          <div className={styles.confirmBody}>{confirmText}</div>
        </ModalShell>
      }
    />
  );
};
